// dashboard/src/pages/apps-setup/LoaderPanel.tsx
// The loader screen a build shows while it starts: a portrait and a landscape
// background, the line of text under the progress bar and an optional icon
// on the bar. Shown when the app's repo type sends any of them (Repos Setup
// tab), and only the parts it sends; the others stay kept on the app, so
// switching a rule back on needs no re-upload. Uploads are cropped, scaled and compressed to CI's shape on the
// server; every part can also be drawn by AI, one at a time or all at once.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  uploadLoaderImage, deleteLoaderImage, saveLoaderText, generateLoaderPart, REPO_ITEM_LABELS,
  type AppLoader, type LoaderImage, type LoaderKind, type RepoRule, type RepoTypeRules,
} from '../../api/app-builds';

const RULE_NOTE: Record<RepoRule, string> = { req: 'required', opt: 'optional', off: 'not sent for this type' };

const KINDS = ['portrait', 'landscape', 'icon'] as const;

/** How often "Generate all" leaves the icon out, for variety, where the type does not require one. */
const NO_ICON_SHARE = 0.25;
/** How often a drawn background carries the app name, when left to chance (matches the API). */
const NAME_SHARE = 0.4;

type NameChoice = 'random' | 'yes' | 'no';

const KIND_META: Record<LoaderKind, { label: string; size: string; rule: keyof RepoTypeRules; box: string }> = {
  icon: { label: 'Icon', size: 'PNG, up to 256 px', rule: 'loader_icon', box: 'h-20 w-20' },
  portrait: { label: 'Portrait', size: '1125×2436 JPEG', rule: 'loader_bg', box: 'h-48 aspect-[1125/2436]' },
  landscape: { label: 'Landscape', size: '2436×1125 JPEG', rule: 'loader_bg', box: 'h-24 aspect-[2436/1125]' },
};

// Transparent icons read better over a checkerboard.
const CHECKER = {
  backgroundImage: 'conic-gradient(#8884 25%, transparent 0 50%, #8884 0 75%, transparent 0)',
  backgroundSize: '12px 12px',
};

function kb(bytes: number | null | undefined): string {
  return bytes == null ? '' : `${Math.round(bytes / 1024)} KB`;
}

function LoaderImageSlot({
  appId, kind, image, rule, drawing, onDraw,
}: {
  appId: string; kind: LoaderKind; image: LoaderImage | null; rule: RepoRule; drawing: boolean; onDraw: () => void;
}) {
  const qc = useQueryClient();
  const meta = KIND_META[kind];
  const refresh = () => qc.invalidateQueries({ queryKey: ['build-config', appId] });
  const upload = useMutation({ mutationFn: (f: File) => uploadLoaderImage(appId, kind, f), onSettled: refresh });
  const remove = useMutation({ mutationFn: () => deleteLoaderImage(appId, kind), onSettled: refresh });
  const error = upload.error ?? remove.error;
  const busy = upload.isPending || remove.isPending || drawing;

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-content-subtle">
        {meta.label} · <span className={rule === 'req' && !image ? 'text-red-500' : ''}>{RULE_NOTE[rule]}</span>
      </div>
      <div
        className={`${meta.box} rounded border ${image ? 'border-border' : 'border-dashed border-border'} overflow-hidden bg-surface-muted flex items-center justify-center text-xs text-content-subtle`}
        style={kind === 'icon' && image ? CHECKER : undefined}
      >
        {drawing ? 'Drawing…' : image ? (
          <a href={image.url} target="_blank" rel="noreferrer" className="h-full w-full">
            <img src={image.url} alt={`Loader ${meta.label.toLowerCase()}`} className={`h-full w-full ${kind === 'icon' ? 'object-contain' : 'object-cover'}`} />
          </a>
        ) : 'none'}
      </div>
      <div className="text-[11px] text-content-subtle">{image ? `${meta.size} · ${kb(image.bytes)}` : meta.size}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <label className={`text-primary hover:underline ${busy ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}>
          {upload.isPending ? 'Uploading…' : image ? 'Replace' : 'Upload'}
          <input
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload.mutate(f); }}
          />
        </label>
        <button type="button" disabled={busy} onClick={onDraw} className="text-primary hover:underline disabled:opacity-40">
          {image ? 'Redraw with AI' : 'Draw with AI'}
        </button>
        {image && (
          <button
            type="button"
            disabled={busy}
            onClick={() => { if (window.confirm(`Remove the loader ${meta.label.toLowerCase()}?`)) remove.mutate(); }}
            className="text-content-muted hover:text-red-500 disabled:opacity-40"
          >
            Remove
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-500 max-w-[16rem]">{(error as Error).message}</p>}
    </div>
  );
}

export function LoaderPanel({ appId, loader, rules }: { appId: string; loader: AppLoader | undefined; rules: RepoTypeRules }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['build-config', appId] });
  const [text, setText] = useState('');
  useEffect(() => { setText(loader?.text ?? ''); }, [loader?.text]);
  const [brief, setBrief] = useState('');
  const [nameChoice, setNameChoice] = useState<NameChoice>('random');
  // What is being drawn right now, and what the last "Generate all" did.
  const [drawing, setDrawing] = useState<Set<'text' | LoaderKind>>(new Set());
  const [status, setStatus] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  const save = useMutation({ mutationFn: () => saveLoaderText(appId, text), onSettled: refresh });
  const textChanged = text.trim() !== (loader?.text ?? '');

  const withName = (part: LoaderKind): boolean | undefined =>
    part === 'icon' || nameChoice === 'random' ? undefined : nameChoice === 'yes';

  async function draw(part: 'text' | LoaderKind, name?: boolean) {
    setDrawing((s) => new Set(s).add(part));
    try {
      const out = await generateLoaderPart(appId, part, { brief: brief.trim() || undefined, with_name: name });
      if (part === 'text' && out.text) setText(out.text);
      return out;
    } finally {
      setDrawing((s) => { const n = new Set(s); n.delete(part); return n; });
      refresh();
    }
  }

  async function drawOne(part: 'text' | LoaderKind) {
    setGenError(null);
    try { await draw(part, part === 'text' ? undefined : withName(part)); } catch (e) { setGenError((e as Error).message); }
  }

  // Text and icon alongside the portrait; the landscape after it, since it is drawn from it.
  async function generateAll() {
    setGenError(null);
    const name = nameChoice === 'random' ? Math.random() < NAME_SHARE : nameChoice === 'yes';
    const skipIcon = rules.loader_icon === 'opt' && Math.random() < NO_ICON_SHARE;
    const notes: string[] = [];
    const jobs: Promise<unknown>[] = [];
    if (rules.loader_text !== 'off') jobs.push(draw('text'));
    if (rules.loader_icon !== 'off') {
      if (skipIcon) {
        notes.push('no icon this time (the bar goes without one)');
        if (loader?.icon) jobs.push(deleteLoaderImage(appId, 'icon').then(refresh));
      } else {
        jobs.push(draw('icon').then((o) => { if (o.motif) notes.push(`icon: ${o.motif}`); }));
      }
    }
    if (rules.loader_bg !== 'off') {
      notes.push(name ? 'app name on the art' : 'no text on the art');
      jobs.push(draw('portrait', name).then(() => draw('landscape', name)));
    }
    setStatus('Drawing… about a minute');
    const results = await Promise.allSettled(jobs);
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failed.length) setGenError(failed.map((f) => (f.reason as Error).message).join(' · '));
    setStatus(notes.length ? `Done: ${notes.join(', ')}` : 'Done');
  }

  const anyDrawing = drawing.size > 0;
  const nothingOn = rules.loader_icon === 'off' && rules.loader_text === 'off' && rules.loader_bg === 'off';

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium text-content">Loader</h3>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1 text-content-muted">
            App name on the art
            <select
              value={nameChoice}
              onChange={(e) => setNameChoice(e.target.value as NameChoice)}
              className="px-1.5 py-1 rounded border border-border bg-surface-muted text-content"
            >
              <option value="random">sometimes</option>
              <option value="yes">yes</option>
              <option value="no">no</option>
            </select>
          </label>
          <button
            type="button"
            disabled={anyDrawing || nothingOn}
            onClick={() => { void generateAll(); }}
            className="px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
          >
            {anyDrawing ? 'Drawing…' : 'Generate all with AI'}
          </button>
        </div>
      </div>
      <input
        value={brief}
        maxLength={1000}
        onChange={(e) => setBrief(e.target.value)}
        placeholder="Wishes for the AI art, optional (e.g. night Vegas, purple neon, golden dragon)"
        aria-label="Wishes for the AI art"
        className="w-full px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
      />
      {(status || genError) && (
        <div className="text-xs space-y-0.5">
          {status && <p className="text-content-muted">{status}</p>}
          {genError && <p className="text-red-500">{genError}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-6">
        {KINDS.filter((kind) => rules[KIND_META[kind].rule] !== 'off').map((kind) => (
          <LoaderImageSlot
            key={kind}
            appId={appId}
            kind={kind}
            image={loader?.[kind] ?? null}
            rule={rules[KIND_META[kind].rule]}
            drawing={drawing.has(kind)}
            onDraw={() => { void drawOne(kind); }}
          />
        ))}
      </div>

      {rules.loader_text !== 'off' && (
        <div className="space-y-1">
          <div className="text-xs text-content-subtle">
            {REPO_ITEM_LABELS.loader_text.name} ·{' '}
            <span className={rules.loader_text === 'req' && !loader?.text ? 'text-red-500' : ''}>{RULE_NOTE[rules.loader_text]}</span>
          </div>
          <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <input
              value={text}
              maxLength={500}
              onChange={(e) => setText(e.target.value)}
              placeholder="Get Ready For Mega Wins!"
              aria-label="Loader text"
              className="flex-1 min-w-[12rem] px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
            />
            <button
              type="submit"
              disabled={!textChanged || save.isPending}
              className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={drawing.has('text')}
              onClick={() => { void drawOne('text'); }}
              className="px-3 py-1.5 text-sm rounded-md border border-border text-primary hover:bg-surface-muted disabled:opacity-40"
            >
              {drawing.has('text') ? 'Writing…' : 'Suggest with AI'}
            </button>
          </form>
          {save.error && <p className="text-xs text-red-500">{(save.error as Error).message}</p>}
        </div>
      )}
      <p className="text-xs text-content-subtle">
        Uploads of any size are cropped to the slot and compressed to at most 300 KB; the icon keeps its transparency.
        The landscape is drawn from the portrait, the portrait from the app icon. Applies from the next build.
      </p>
    </section>
  );
}
